import { useState, useEffect } from 'react'
import ReceiptSettingsTab from './ReceiptSettingsTab'
import type { PrinterConfig, PrinterDevice, BillCounterConfig } from '@abcpay/shared'
import { useCustomerDisplay } from '../hooks/useCustomerDisplay'
import { useEnvironment } from '../hooks/useEnvironment'
import {
  getAvailablePrinters,
  loadPrinterSettings,
  savePrinterSettings,
  testPrinter as testPrinterAPI,
} from '../utils/settingsManager'
import { checkCameraPermission, getAvailableCameras, testCamera } from '../utils/cameraUtils'
import type { CameraDevice } from '../utils/cameraUtils'
import {
  loadCounterSettings,
  saveCounterSettings,
  testCounter,
  startCounter,
} from '../utils/counterManager'

interface PrinterSettingsModalProps {
  isOpen: boolean
  onClose: () => void
}

function PrinterSettingsModal({ isOpen, onClose }: PrinterSettingsModalProps) {
  const [activeTab, setActiveTab] = useState<'printer' | 'receipt' | 'camera' | 'counter' | 'display'>('printer')

  // Customer display
  const {
    isOpen: isDisplayOpen,
    isLoading: isDisplayLoading,
    error: displayError,
    isAvailable: isDisplayAvailable,
    open: openDisplay,
    close: closeDisplay,
  } = useCustomerDisplay()

  const { shellVersion, isElectron } = useEnvironment()

  // Printer state
  const [availablePorts, setAvailablePorts] = useState<PrinterDevice[]>([])
  const [selectedPort, setSelectedPort] = useState<PrinterDevice | null>(null)
  const [baudRate, setBaudRate] = useState<number>(9600)
  const [paperWidth, setPaperWidth] = useState<58 | 80>(58)
  const [isLoading, setIsLoading] = useState(false)
  const [testStatus, setTestStatus] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Camera state
  const [cameras, setCameras] = useState<CameraDevice[]>([])
  const [selectedCamera, setSelectedCamera] = useState<CameraDevice | null>(null)
  const [cameraPermission, setCameraPermission] = useState<'checking' | 'granted' | 'denied' | 'prompt'>('checking')
  const [cameraTestStatus, setCameraTestStatus] = useState<string | null>(null)

  // Counter state
  const [counterPort, setCounterPort] = useState<PrinterDevice | null>(null)
  const [counterBaudRate, setCounterBaudRate] = useState<number>(19200)
  const [counterTestStatus, setCounterTestStatus] = useState<string | null>(null)
  const [counterError, setCounterError] = useState<string | null>(null)

  const loadPorts = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const ports = await getAvailablePrinters()
      setAvailablePorts(ports)
      if (ports.length === 0) {
        setError('시리얼 포트를 찾을 수 없습니다. 프린터 연결을 확인해주세요.')
      }
    } catch (err) {
      setError('시리얼 포트 목록 로드 실패')
      console.error(err)
    } finally {
      setIsLoading(false)
    }
  }

  const loadCurrentSettings = async () => {
    try {
      const settings = await loadPrinterSettings()
      setPaperWidth(settings.paperWidth as 58 | 80)
      setBaudRate(settings.baudRate)

      if (settings.serialPort) {
        const port = availablePorts.find((p) => p.path === settings.serialPort)
        if (port) {
          setSelectedPort(port)
        }
      }
    } catch (err) {
      console.error('설정 로드 실패:', err)
    }
  }

  const loadCurrentCounterSettings = async () => {
    try {
      const settings = await loadCounterSettings()
      setCounterBaudRate(settings.baudRate)

      if (settings.serialPort) {
        const port = availablePorts.find((p) => p.path === settings.serialPort)
        if (port) {
          setCounterPort(port)
        }
      }
    } catch (err) {
      console.error('계수기 설정 로드 실패:', err)
    }
  }

  useEffect(() => {
    if (isOpen) {
      loadPorts()
      setTestStatus(null)
      setError(null)
      setCounterTestStatus(null)
      checkPermissionAndLoadCameras()
    }
  }, [isOpen])

  async function checkPermissionAndLoadCameras() {
    const permission = await checkCameraPermission()

    if (permission.granted) {
      setCameraPermission('granted')
      const cameraList = await getAvailableCameras()
      setCameras(cameraList)

      if (cameraList.length > 0) {
        setSelectedCamera(cameraList[0])
      }
    } else if (permission.denied) {
      setCameraPermission('denied')
    } else {
      setCameraPermission('prompt')
    }
  }

  async function handleCameraTest() {
    if (!selectedCamera) return

    setCameraTestStatus('테스트 중...')
    const success = await testCamera(selectedCamera.deviceId)

    if (success) {
      setCameraTestStatus('✓ 카메라 정상 작동')
    } else {
      setCameraTestStatus('✗ 카메라 접근 실패')
    }
  }

  async function requestCameraPermission() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true })
      stream.getTracks().forEach((track) => track.stop())
      checkPermissionAndLoadCameras()
    } catch {
      setCameraPermission('denied')
    }
  }

  useEffect(() => {
    if (availablePorts.length > 0) {
      loadCurrentSettings()
      loadCurrentCounterSettings()
    }
  }, [availablePorts])

  const handlePortSelect = (portPath: string) => {
    const port = availablePorts.find((p) => p.path === portPath)
    setSelectedPort(port || null)
    setTestStatus(null)
  }

  const handleTestPrint = async () => {
    if (!selectedPort) {
      setError('시리얼 포트를 선택해주세요')
      return
    }

    setTestStatus('테스트 출력 중...')
    setError(null)

    try {
      await savePrinterSettings({
        deviceName: selectedPort.name,
        serialPort: selectedPort.path,
        baudRate,
        dataBits: 8,
        parity: 'none',
        stopBits: 1,
        paperWidth,
        charactersPerLine: paperWidth === 58 ? 32 : 42,
      })

      const result = await testPrinterAPI()
      if (result.success) {
        setTestStatus('✓ 테스트 출력 성공')
      } else {
        setTestStatus(`✗ 테스트 출력 실패: ${result.message}`)
        setError(result.error || result.message)
      }
    } catch (err) {
      setTestStatus('✗ 테스트 출력 오류')
      setError(err instanceof Error ? err.message : String(err))
      console.error(err)
    }
  }

  const handleSave = async () => {
    if (!selectedPort) {
      setError('시리얼 포트를 선택해주세요')
      return
    }

    try {
      const config: PrinterConfig = {
        deviceName: selectedPort.name,
        serialPort: selectedPort.path,
        baudRate,
        dataBits: 8,
        parity: 'none',
        stopBits: 1,
        paperWidth,
        charactersPerLine: paperWidth === 58 ? 32 : 42,
      }

      const result = await savePrinterSettings(config)
      if (result.success) {
        onClose()
        window.focus()
      } else {
        setError(result.message || '설정 저장 실패')
      }
    } catch (err) {
      setError('설정 저장 중 오류 발생')
      console.error(err)
    }
  }

  const handleCounterTest = async () => {
    if (!counterPort) return

    setCounterTestStatus('연결 테스트 중...')
    setCounterError(null)

    try {
      const config: BillCounterConfig = {
        serialPort: counterPort.path,
        baudRate: counterBaudRate,
        dataBits: 8,
        parity: 'none',
        stopBits: 1,
        enabled: true,
      }
      const result = await testCounter(config)
      if (result.connected) {
        setCounterTestStatus('✓ 계수기 연결 성공')
      } else {
        setCounterTestStatus(`✗ 연결 실패: ${result.error || '알 수 없는 오류'}`)
      }
    } catch (err) {
      setCounterTestStatus('✗ 연결 테스트 오류')
      setCounterError(err instanceof Error ? err.message : String(err))
    }
  }

  const handleCounterSave = async () => {
    if (!counterPort) {
      setCounterError('시리얼 포트를 선택해주세요')
      return
    }

    try {
      const config: BillCounterConfig = {
        serialPort: counterPort.path,
        baudRate: counterBaudRate,
        dataBits: 8,
        parity: 'none',
        stopBits: 1,
        enabled: true,
      }
      const result = await saveCounterSettings(config)
      if (result.success) {
        await startCounter(config)
        alert('계수기 설정이 저장되었습니다. 연결을 시작합니다.')
      } else {
        setCounterError(result.message || '설정 저장 실패')
      }
    } catch (err) {
      setCounterError('설정 저장 중 오류 발생')
      console.error(err)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />

      <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">설정</h2>

        <div className="flex overflow-x-auto whitespace-nowrap border-b border-gray-200 mb-4">
          <button
            onClick={() => setActiveTab('printer')}
            className={`px-4 py-2 border-b-2 transition-colors ${
              activeTab === 'printer'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            프린터
          </button>
          <button
            onClick={() => setActiveTab('receipt')}
            className={`px-4 py-2 border-b-2 transition-colors ${
              activeTab === 'receipt'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            영수증 정보
          </button>
          <button
            onClick={() => setActiveTab('camera')}
            className={`px-4 py-2 border-b-2 transition-colors ${
              activeTab === 'camera'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            웹캠
          </button>
          <button
            onClick={() => setActiveTab('counter')}
            className={`px-4 py-2 border-b-2 transition-colors ${
              activeTab === 'counter'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            계수기
          </button>
          <button
            onClick={() => setActiveTab('display')}
            className={`px-4 py-2 border-b-2 transition-colors ${
              activeTab === 'display'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            디스플레이
          </button>
        </div>

        {activeTab === 'receipt' && <ReceiptSettingsTab />}

        {activeTab === 'printer' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                시리얼 포트 선택
                <button
                  onClick={loadPorts}
                  disabled={isLoading}
                  className="ml-2 text-xs text-blue-500 hover:text-blue-700 disabled:text-gray-400"
                >
                  {isLoading ? '감지 중...' : '새로고침'}
                </button>
              </label>
              <select
                value={selectedPort?.path || ''}
                onChange={(e) => handlePortSelect(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                disabled={isLoading || availablePorts.length === 0}
              >
                <option value="">시리얼 포트를 선택하세요</option>
                {availablePorts.map((port) => (
                  <option key={port.path} value={port.path}>
                    {port.displayName || port.path}
                  </option>
                ))}
              </select>
            </div>

            {selectedPort && (
              <div className="bg-gray-50 p-4 rounded-md text-sm space-y-2">
                <div className="font-semibold text-gray-900 text-base">
                  {selectedPort.displayName || selectedPort.name}
                </div>

                <div className="space-y-1 text-xs text-gray-600">
                  <div className="flex justify-between">
                    <span>포트 경로:</span>
                    <span className="font-mono text-gray-800">{selectedPort.path}</span>
                  </div>

                  {selectedPort.manufacturer && (
                    <div className="flex justify-between">
                      <span>제조사:</span>
                      <span className="font-mono text-gray-800">{selectedPort.manufacturer}</span>
                    </div>
                  )}

                  {selectedPort.serialNumber && (
                    <div className="flex justify-between">
                      <span>시리얼 번호:</span>
                      <span className="font-mono text-gray-800">{selectedPort.serialNumber}</span>
                    </div>
                  )}

                  <div className="mt-2 pt-2 border-t border-gray-300">
                    <div className="font-medium text-gray-700 mb-1">시리얼 인터페이스:</div>
                    <div className="font-mono text-gray-900 bg-white px-2 py-1 rounded">
                      serial://{selectedPort.path}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">통신 속도 (Baud Rate)</label>
              <div className="grid grid-cols-3 gap-2">
                {[9600, 19200, 38400, 57600, 115200].map((rate) => (
                  <label key={rate} className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      value={rate}
                      checked={baudRate === rate}
                      onChange={() => setBaudRate(rate)}
                      className="w-4 h-4"
                    />
                    <span>{rate === 9600 ? `${rate} (권장)` : rate}</span>
                  </label>
                ))}
              </div>
              <div className="mt-2 text-xs text-gray-500">프로토콜: 8N1 (8 data bits, No parity, 1 stop bit)</div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">용지 크기</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    value="58"
                    checked={paperWidth === 58}
                    onChange={() => setPaperWidth(58)}
                    className="w-4 h-4"
                  />
                  <span>58mm (32자/줄)</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    value="80"
                    checked={paperWidth === 80}
                    onChange={() => setPaperWidth(80)}
                    className="w-4 h-4"
                  />
                  <span>80mm (42자/줄)</span>
                </label>
              </div>
            </div>

            <div>
              <button
                onClick={handleTestPrint}
                disabled={!selectedPort || isLoading}
                className={`w-full px-4 py-2 rounded-md transition-colors ${
                  !selectedPort || isLoading
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-500 text-white hover:bg-blue-600'
                }`}
              >
                테스트 출력
              </button>
              {testStatus && (
                <div className={`text-sm mt-2 ${testStatus.includes('✓') ? 'text-green-600' : 'text-red-600'}`}>
                  {testStatus}
                </div>
              )}
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">
                {error}
              </div>
            )}
          </div>
        )}

        {activeTab === 'camera' && (
          <div className="space-y-4">
            {cameraPermission === 'checking' && (
              <div className="text-gray-600 text-center py-4">카메라 권한 확인 중...</div>
            )}

            {cameraPermission === 'denied' && (
              <div className="bg-red-50 border border-red-200 p-4 rounded-md">
                <p className="text-sm text-red-700">
                  카메라 접근 권한이 거부되었습니다.
                  브라우저 설정에서 카메라 권한을 허용해주세요.
                </p>
              </div>
            )}

            {cameraPermission === 'prompt' && (
              <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-md">
                <p className="text-sm text-yellow-700 mb-3">카메라 접근 권한이 필요합니다.</p>
                <button
                  onClick={requestCameraPermission}
                  className="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600 transition-colors"
                >
                  권한 요청
                </button>
              </div>
            )}

            {cameraPermission === 'granted' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    카메라 선택
                    <button
                      onClick={checkPermissionAndLoadCameras}
                      className="ml-2 text-xs text-blue-500 hover:text-blue-700"
                    >
                      새로고침
                    </button>
                  </label>
                  <select
                    value={selectedCamera?.deviceId || ''}
                    onChange={(e) => {
                      const camera = cameras.find((c) => c.deviceId === e.target.value)
                      setSelectedCamera(camera || null)
                      setCameraTestStatus(null)
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">카메라를 선택하세요</option>
                    {cameras.map((camera) => (
                      <option key={camera.deviceId} value={camera.deviceId}>
                        {camera.label}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedCamera && (
                  <div className="bg-gray-50 p-4 rounded-md text-sm space-y-2">
                    <div className="font-semibold text-gray-900 text-base">{selectedCamera.label}</div>
                    <div className="text-xs text-gray-600">
                      Device ID: <span className="font-mono text-gray-800">{selectedCamera.deviceId}</span>
                    </div>
                  </div>
                )}

                <div>
                  <button
                    onClick={handleCameraTest}
                    disabled={!selectedCamera}
                    className={`w-full px-4 py-2 rounded-md transition-colors ${
                      !selectedCamera
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-500 text-white hover:bg-blue-600'
                    }`}
                  >
                    카메라 테스트
                  </button>
                  {cameraTestStatus && (
                    <div className={`text-sm mt-2 ${cameraTestStatus.includes('✓') ? 'text-green-600' : 'text-red-600'}`}>
                      {cameraTestStatus}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'counter' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                계수기 시리얼 포트
                <button
                  onClick={loadPorts}
                  disabled={isLoading}
                  className="ml-2 text-xs text-blue-500 hover:text-blue-700 disabled:text-gray-400"
                >
                  {isLoading ? '감지 중...' : '새로고침'}
                </button>
              </label>
              <select
                value={counterPort?.path || ''}
                onChange={(e) => {
                  const port = availablePorts.find((p) => p.path === e.target.value)
                  setCounterPort(port || null)
                  setCounterTestStatus(null)
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                disabled={isLoading || availablePorts.length === 0}
              >
                <option value="">시리얼 포트를 선택하세요</option>
                {availablePorts.map((port) => (
                  <option key={port.path} value={port.path}>
                    {port.displayName || port.path}
                    {selectedPort?.path === port.path ? ' (프린터 사용 중)' : ''}
                  </option>
                ))}
              </select>
              {counterPort && selectedPort && counterPort.path === selectedPort.path && (
                <p className="text-xs text-orange-600 mt-1">⚠ 이 포트는 프린터에서도 사용 중입니다. 다른 포트를 선택하세요.</p>
              )}
            </div>

            {counterPort && (
              <div className="bg-gray-50 p-4 rounded-md text-sm space-y-2">
                <div className="font-semibold text-gray-900 text-base">{counterPort.displayName || counterPort.name}</div>
                <div className="text-xs text-gray-600">
                  <div className="flex justify-between">
                    <span>포트 경로:</span>
                    <span className="font-mono text-gray-800">{counterPort.path}</span>
                  </div>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">통신 속도 (Baud Rate)</label>
              <div className="grid grid-cols-3 gap-2">
                {[9600, 19200, 38400].map((rate) => (
                  <label key={rate} className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      value={rate}
                      checked={counterBaudRate === rate}
                      onChange={() => setCounterBaudRate(rate)}
                      className="w-4 h-4"
                    />
                    <span>{rate === 19200 ? `${rate} (권장)` : rate}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <button
                onClick={handleCounterTest}
                disabled={!counterPort || isLoading}
                className={`w-full px-4 py-2 rounded-md transition-colors ${
                  !counterPort || isLoading
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-500 text-white hover:bg-blue-600'
                }`}
              >
                연결 테스트
              </button>
              {counterTestStatus && (
                <div className={`text-sm mt-2 ${counterTestStatus.includes('✓') ? 'text-green-600' : 'text-red-600'}`}>
                  {counterTestStatus}
                </div>
              )}
            </div>

            <div>
              <button
                onClick={handleCounterSave}
                disabled={!counterPort || isLoading}
                className={`w-full px-4 py-2 rounded-md transition-colors ${
                  !counterPort || isLoading
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-red-500 text-white hover:bg-red-600'
                }`}
              >
                계수기 설정 저장
              </button>
            </div>

            {counterError && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">{counterError}</div>
            )}
          </div>
        )}

        {activeTab === 'display' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">고객용 디스플레이</label>
              <p className="text-xs text-gray-500 mb-4">
                외부 모니터에 고객용 화면을 표시합니다. 환율 정보, 거래 진행 상황, 계수기 데이터가 실시간으로 표시됩니다.
              </p>
            </div>

            <div className="bg-gray-50 p-4 rounded-md space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">현재 상태</span>
                <span className={`text-sm font-semibold ${isDisplayOpen ? 'text-green-600' : 'text-gray-500'}`}>
                  {isDisplayOpen ? '열림' : '닫힘'}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">외부 디스플레이</span>
                <span className={`text-sm ${isDisplayAvailable ? 'text-green-600' : 'text-orange-500'}`}>
                  {isDisplayAvailable ? '감지됨' : '미감지'}
                </span>
              </div>
            </div>

            {!isDisplayAvailable && (
              <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-md">
                <p className="text-sm text-yellow-700">
                  외부 모니터가 연결되어 있지 않거나, POS 앱(Electron) 환경에서만 사용 가능합니다.
                </p>
              </div>
            )}

            <div>
              {isDisplayOpen ? (
                <button
                  onClick={closeDisplay}
                  disabled={isDisplayLoading}
                  className={`w-full px-4 py-2 rounded-md transition-colors ${
                    isDisplayLoading
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-red-500 text-white hover:bg-red-600'
                  }`}
                >
                  {isDisplayLoading ? '처리 중...' : '고객 화면 닫기'}
                </button>
              ) : (
                <button
                  onClick={openDisplay}
                  disabled={isDisplayLoading || !isDisplayAvailable}
                  className={`w-full px-4 py-2 rounded-md transition-colors ${
                    isDisplayLoading || !isDisplayAvailable
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-500 text-white hover:bg-blue-600'
                  }`}
                >
                  {isDisplayLoading ? '처리 중...' : '고객 화면 열기'}
                </button>
              )}
            </div>

            {displayError && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">
                {displayError}
              </div>
            )}
          </div>
        )}

        {activeTab === 'receipt' ? (
          <button
            onClick={onClose}
            className="w-full mt-6 bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
          >
            닫기
          </button>
        ) : (
          <div className="flex gap-3 mt-6">
            <button
              onClick={onClose}
              className="flex-1 bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition-colors"
            >
              취소
            </button>
            <button
              onClick={handleSave}
              disabled={!selectedPort || isLoading || activeTab === 'display'}
              className={`flex-1 px-4 py-2 rounded-md transition-colors ${
                !selectedPort || isLoading || activeTab === 'display'
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-red-500 text-white hover:bg-red-600'
              }`}
            >
              저장
            </button>
          </div>
        )}

        <div className="mt-4 pt-3 border-t border-gray-200 text-xs text-gray-400 text-center space-x-3">
          <span>web v{__COMMIT_HASH__}</span>
          {isElectron && shellVersion && <span>shell v{shellVersion}</span>}
        </div>
      </div>
    </div>
  )
}

export default PrinterSettingsModal
